/**
 * Relationship Graph
 *
 * Visual canvas of item relationships using React Flow + dagre layout.
 * Drag between nodes opens a type picker covering the full UI taxonomy
 * (edge families + group types: Package, Competing, Cluster, Collection).
 *
 * Design decisions:
 *   - Items are filtered to active only (deferred/archived excluded).
 *   - No status color-coding — edge color encodes relationship type only.
 *   - Card configuration panel lets the user choose which fields appear.
 *   - On-canvas edge drawing opens a type-picker to set the relationship.
 *   - Group types (package/exclusive/cluster/collection) create a group with
 *     both endpoints as members, and are visualized as undirected edges.
 *
 * Feature-flagged extensions (all off by default):
 *   GRAPH_INSPECTOR          — slide-over inspector panel for selected node
 *   GRAPH_PATH_ANALYSIS      — transitive BFS reachability instead of 1-hop
 *   GRAPH_CLUSTER_RESOLUTION — cluster-candidate badge + resolution wizard
 *   GRAPH_NEXUS_OVERLAY      — Nexus agent proposals as ghost edges
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ReactFlow, Background, Controls, MiniMap,
  type Node, type Edge, type NodeTypes, type EdgeTypes,
  Handle, Position, useNodesState, useEdgesState,
  type NodeProps, type EdgeProps, type Connection,
  useReactFlow, ReactFlowProvider,
  MarkerType, Panel, BaseEdge, EdgeLabelRenderer,
  getSmoothStepPath,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from '@dagrejs/dagre';
import {
  GitBranch, Filter, X, ChevronDown, ChevronUp, AlertTriangle,
  Box, MessageSquare, Link2, Layers, Flag, ListTree, Settings2,
  ArrowRight, ArrowUpDown, Check, Loader2, Sparkles, Network,
  ChevronRight, AlertOctagon, Eye, Info, Users, Zap, CornerDownRight,
  Tag, Swords, Weight,
} from 'lucide-react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useNexusRelationshipStore } from '../../stores/prioritization/nexusRelationshipStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useTheme } from '../../contexts/ThemeContext';
import { useFeatureFlag, FEATURE_FLAGS } from '../../lib/featureFlags';
import { loadPoolScopeOptions, computeViolations, type PoolScopeOption, type PoolViolationReport } from '../../lib/poolViolations';
import { getRelationshipGuideEntry } from '../../lib/relationshipGuide';
import { formatCarriesShare } from '../../lib/carriesRelationships';
import { PoolViolationsPanel } from '../../components/prioritization/graph/PoolViolationsPanel';
import { RelationshipGraphListView } from '../../components/prioritization/graph/RelationshipGraphListView';
import { useVisualViewportFlags } from '../../hooks/prioritization/useVisualViewport';
import type { NexusRelationshipProposal } from '../../lib/nexusAgentService';
import type {
  ItemRelationshipEdge,
  ItemRelationshipGroup,
  ItemRelationshipGroupMembership,
  RelationshipGroupType,
} from '../../types/prioritization/itemRelationships';

// ============================================================================
// Relationship types — edge families + group types (canon UI taxonomy)
// ============================================================================

interface EdgeConfig {
  color: string;
  colorLight: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  dashed?: boolean;
  directed?: boolean;
  description: string;
}

const relationshipDefinition = (type: string, fallback: string) =>
  getRelationshipGuideEntry(type)?.tagline ?? fallback;

/** Group types created via createGroup (not createEdge) when chosen in the picker. */
const GROUP_CREATE_TYPES = new Set<RelationshipGroupType>([
  'package',
  'exclusive',
  'cluster',
  'collection',
]);

const IDLE_PILL =
  'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-ter)] dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-ter)] hover:dark:bg-[var(--hover-tint)]';

const EDGE_CONFIG: Record<string, EdgeConfig> = {
  dependency:  {
    color: '#3b82f6', colorLight: '#eff6ff',
    label: 'Dependency', icon: GitBranch, directed: true,
    description: relationshipDefinition('dependency', 'This item depends on the other'),
  },
  prerequisite: {
    color: '#8b5cf6', colorLight: '#f5f3ff',
    label: 'Prerequisite', icon: Flag, directed: true,
    description: relationshipDefinition('prerequisite', 'Must be done before this item'),
  },
  aggregation: {
    color: '#10b981', colorLight: '#ecfdf5',
    label: 'Aggregation', icon: ArrowUpDown,
    description: relationshipDefinition('aggregation', 'This item rolls up into the other'),
  },
  breakdown: {
    color: '#f59e0b', colorLight: '#fffbeb',
    label: 'Breakdown', icon: ListTree, directed: true,
    description: relationshipDefinition('breakdown', 'The other item breaks down this one'),
  },
  elaboration: {
    color: '#6366f1', colorLight: '#eef2ff',
    label: 'Elaboration', icon: MessageSquare,
    description: relationshipDefinition('elaboration', 'One item elaborates the other'),
  },
  related: {
    color: '#94a3b8', colorLight: '#f8fafc',
    label: 'Related', icon: Link2, dashed: true,
    description: 'Loosely associated',
  },
  contains: {
    color: '#0ea5e9', colorLight: '#f0f9ff',
    label: 'Contains', icon: CornerDownRight, directed: true,
    description: relationshipDefinition('contains', 'Parent item contains child items (epic → story → task)'),
  },
  reframe: {
    color: '#e11d48', colorLight: '#fff1f2',
    label: 'Additional Category', icon: Eye,
    description: relationshipDefinition('reframe', 'The same item under another category'),
  },
  // Drawn, not offered in the connect picker: a Carries edge needs its share,
  // which is entered on the item page (canon 12 §5.11).
  carries: {
    color: '#d97706', colorLight: '#fffbeb',
    label: 'Carries', icon: Weight, directed: true,
    description: relationshipDefinition('carries', 'Carries part of the worth of the item it enables'),
  },
  // Group types (visualized as undirected pair-edges; created via createGroup)
  package: {
    color: '#f97316', colorLight: '#fff7ed',
    label: 'Package', icon: Box, dashed: true,
    description: relationshipDefinition('package', 'These items must move together.'),
  },
  exclusive: {
    color: '#f43f5e', colorLight: '#fff1f2',
    label: 'Competing', icon: Swords, dashed: true,
    description: relationshipDefinition('exclusive', 'One or the other — not both.'),
  },
  cluster: {
    color: '#06b6d4', colorLight: '#ecfeff',
    label: 'Cluster', icon: Tag, dashed: true,
    description: relationshipDefinition('cluster', 'These items are really the same thing.'),
  },
  collection: {
    color: '#6366f1', colorLight: '#eef2ff',
    label: 'Collection', icon: Layers, dashed: true,
    description: relationshipDefinition('collection', 'I want to keep track of these items together.'),
  },
};

type UiEdgeEntry = EdgeConfig & {
  /** Stored edge type or group type written on select. */
  writeType: string;
  /** Key into EDGE_FILTER_PILL. */
  filterStyle: string;
  /** When true, picker creates a group instead of an edge. */
  isGroup?: boolean;
};

/**
 * UI-facing picker / filter / legend order — mirrors RELATIONSHIP_UI_IDS
 * plus the legacy `related` edge (still used on boards).
 */
const UI_EDGE_TYPE_ORDER = [
  'sequence',
  'composition',
  'package',
  'exclusive',
  'cluster',
  'elaboration',
  'contains',
  'collection',
  'related',
  'reframe',
] as const;

const UI_EDGE_CONFIG: Record<string, UiEdgeEntry> = {
  sequence: {
    ...EDGE_CONFIG.dependency,
    label: getRelationshipGuideEntry('sequence')?.label ?? 'Sequence & conditions',
    description: getRelationshipGuideEntry('sequence')?.tagline ?? 'Predecessors and dependents, regardless of asserted viewpoint',
    icon: getRelationshipGuideEntry('sequence')?.Icon ?? EDGE_CONFIG.dependency.icon,
    writeType: 'dependency',
    filterStyle: 'dependency',
  },
  composition: {
    ...EDGE_CONFIG.aggregation,
    label: getRelationshipGuideEntry('composition')?.label ?? 'Composition',
    description: getRelationshipGuideEntry('composition')?.tagline ?? 'Whole-and-part structure, regardless of authored viewpoint',
    icon: getRelationshipGuideEntry('composition')?.Icon ?? EDGE_CONFIG.aggregation.icon,
    writeType: 'aggregation',
    filterStyle: 'aggregation',
  },
  package: { ...EDGE_CONFIG.package, writeType: 'package', filterStyle: 'package', isGroup: true },
  exclusive: { ...EDGE_CONFIG.exclusive, writeType: 'exclusive', filterStyle: 'exclusive', isGroup: true },
  cluster: { ...EDGE_CONFIG.cluster, writeType: 'cluster', filterStyle: 'cluster', isGroup: true },
  elaboration: { ...EDGE_CONFIG.elaboration, writeType: 'elaboration', filterStyle: 'elaboration' },
  contains: { ...EDGE_CONFIG.contains, writeType: 'contains', filterStyle: 'contains' },
  collection: { ...EDGE_CONFIG.collection, writeType: 'collection', filterStyle: 'collection', isGroup: true },
  related: { ...EDGE_CONFIG.related, writeType: 'related', filterStyle: 'related' },
  reframe: { ...EDGE_CONFIG.reframe, writeType: 'reframe', filterStyle: 'reframe' },
};

function uiTypeForStoredType(type: string): string {
  if (type === 'dependency' || type === 'prerequisite') return 'sequence';
  if (type === 'aggregation' || type === 'breakdown') return 'composition';
  if (type === 'related_group') return 'related';
  return UI_EDGE_CONFIG[type] ? type : 'related';
}

/** Filter pill styles — aligned with Sidebar relationship nav icons; dark-mode safe. */
const EDGE_FILTER_PILL: Record<string, { active: string; idle: string }> = {
  dependency: {
    active:
      'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)]/50 dark:bg-[var(--slate)]/45',
    idle: IDLE_PILL,
  },
  prerequisite: {
    active:
      'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)]/50 dark:bg-[var(--slate)]/45',
    idle: IDLE_PILL,
  },
  aggregation: {
    active:
      'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:border-[var(--green)]/50 dark:bg-[var(--green)]/45 dark:text-[var(--text-ter)]',
    idle: IDLE_PILL,
  },
  breakdown: {
    active:
      'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)] dark:border-[var(--mustard)]/50 dark:bg-[var(--mustard)]/45 dark:text-[var(--mustard)]',
    idle: IDLE_PILL,
  },
  elaboration: {
    active:
      'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)]/50 dark:bg-[var(--slate)]/45',
    idle: IDLE_PILL,
  },
  related: {
    active:
      'border-[var(--rule)] bg-[var(--hover-tint)] text-[var(--text-sec)] dark:border-[var(--rule)]/50 dark:bg-[var(--bg-raised)]/60',
    idle: IDLE_PILL,
  },
  contains: {
    active:
      'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)]/50 dark:bg-[var(--slate)]/45',
    idle: IDLE_PILL,
  },
  reframe: {
    active:
      'border-[var(--coral)] bg-[var(--coral-dim)] text-[var(--coral)] dark:border-[var(--coral)]/50 dark:bg-[var(--coral)]/45 dark:text-[var(--coral)]',
    idle: IDLE_PILL,
  },
  package: {
    active:
      'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)] dark:border-[var(--mustard)]/50 dark:bg-[var(--mustard)]/45 dark:text-[var(--mustard)]',
    idle: IDLE_PILL,
  },
  exclusive: {
    active:
      'border-[var(--coral)] bg-[var(--coral-dim)] text-[var(--coral)] dark:border-[var(--coral)]/50 dark:bg-[var(--coral)]/45 dark:text-[var(--coral)]',
    idle: IDLE_PILL,
  },
  cluster: {
    active:
      'border-cyan-400 bg-cyan-100 text-cyan-700 dark:border-cyan-500/50 dark:bg-cyan-950/45 dark:text-cyan-300',
    idle: IDLE_PILL,
  },
  collection: {
    active:
      'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)]/50 dark:bg-[var(--slate)]/45',
    idle: IDLE_PILL,
  },
};

/** Turn active groups into undirected pair-edges so they appear on the canvas. */
function synthesizeGroupEdges(
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
): ItemRelationshipEdge[] {
  const membersByGroup = new Map<string, string[]>();
  for (const m of memberships) {
    const list = membersByGroup.get(m.groupId) ?? [];
    list.push(m.itemId);
    membersByGroup.set(m.groupId, list);
  }

  const out: ItemRelationshipEdge[] = [];
  for (const g of groups) {
    if (g.status === 'dissolved' || g.status === 'dismissed') continue;
    const uiType =
      g.type === 'related_group'
        ? 'related'
        : GROUP_CREATE_TYPES.has(g.type)
          ? g.type
          : null;
    if (!uiType) continue;

    const members = membersByGroup.get(g.id) ?? [];
    for (let i = 0; i < members.length; i++) {
      for (let j = i + 1; j < members.length; j++) {
        out.push({
          id: `group-edge-${g.id}-${members[i]}-${members[j]}`,
          sourceItemId: members[i],
          targetItemId: members[j],
          type: uiType as ItemRelationshipEdge['type'],
          status: g.status === 'candidate' ? 'candidate' : 'active',
          directionality: 'undirected',
          createdAt: g.createdAt,
          // Display-only marker so the graph can dissolve the owning group.
          _groupId: g.id,
        } as ItemRelationshipEdge & { _groupId: string });
      }
    }
  }
  return out;
}

const NODE_WIDTH  = 220;
const NODE_HEIGHT = 76;
const ITEM_CAP    = 80;

// ============================================================================
// Card configuration
// ============================================================================

interface CardConfig {
  showCategory:        boolean;
  showAltitude:        boolean;
  showConnectionCount: boolean;
}

const DEFAULT_CARD_CONFIG: CardConfig = {
  showCategory:        true,
  showAltitude:        false,
  showConnectionCount: true,
};

// ============================================================================
// Custom Node — shows only what card config enables
// ============================================================================

function ItemNode({ data }: NodeProps) {
  const d = data as {
    label:              string;
    category:           string;
    altitude:           string | null;
    id:                 string;
    connectionCount:    number;
    dimmed:             boolean;
    cardConfig:         CardConfig;
    // Path analysis
    pathDistance?:      number | null;
    // Cluster resolution
    clusterGroupId?:    string | null;
    onResolveCluster?:  (() => void) | null;
    // Pool violations (GRAPH_POOL_VIOLATIONS)
    violation?:         { disposition: 'block' | 'warn'; patterns: string[] } | null;
    poolDimmed?:        boolean;
  };

  const cfg = d.cardConfig ?? DEFAULT_CARD_CONFIG;

  const handleCls = '!bg-[var(--slate)] !border-white !w-2.5 !h-2.5 !opacity-0 group-hover:!opacity-100 transition-opacity';

  // Path analysis border tinting
  let borderColor = d.dimmed ? '#e5e7eb' : '#e2e8f0';
  if (d.pathDistance === 0) borderColor = '#6366f1'; // source = indigo
  else if (d.pathDistance === 1) borderColor = '#3b82f6'; // hop 1 = blue
  else if (d.pathDistance === 2) borderColor = '#06b6d4'; // hop 2 = cyan

  // Pool-violation ring (takes visual precedence)
  const vColor = d.violation ? (d.violation.disposition === 'block' ? '#ef4444' : '#f59e0b') : null;
  if (vColor) borderColor = vColor;

  return (
    <div
      className="relative bg-[var(--bg-raised)] border-2 px-3 py-2.5 shadow-sm transition-all cursor-pointer group"
      style={{
        minWidth: 150,
        maxWidth: NODE_WIDTH,
        borderColor,
        opacity: d.dimmed ? 0.2 : (d.poolDimmed ? 0.28 : 1),
        boxShadow: vColor ? `0 0 0 3px ${vColor}33` : undefined,
      }}
    >
      {/* Pool-violation badge */}
      {d.violation && (
        <div
          className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-bold leading-none border shadow-sm z-10"
          style={{ backgroundColor: vColor!, color: '#fff', borderColor: '#fff' }}
          title={d.violation.patterns.join(' · ')}
        >
          <AlertOctagon className="h-2.5 w-2.5" />
          {d.violation.patterns.length}
        </div>
      )}
      {/* All 4 sides — source + target on each so edges can start/end anywhere */}
      <Handle type="source" position={Position.Top}    id="s-top"    className={handleCls} />
      <Handle type="target" position={Position.Top}    id="t-top"    className={handleCls} />
      <Handle type="source" position={Position.Bottom} id="s-bottom" className={handleCls} />
      <Handle type="target" position={Position.Bottom} id="t-bottom" className={handleCls} />
      <Handle type="source" position={Position.Left}   id="s-left"   className={handleCls} />
      <Handle type="target" position={Position.Left}   id="t-left"   className={handleCls} />
      <Handle type="source" position={Position.Right}  id="s-right"  className={handleCls} />
      <Handle type="target" position={Position.Right}  id="t-right"  className={handleCls} />

      {/* Header row */}
      <div className="flex items-center justify-between gap-1.5 mb-1">
        {cfg.showCategory && d.category && (
          <span className="text-[9px] font-semibold text-[var(--text-ter)] uppercase tracking-wide truncate leading-none">
            {d.category}
          </span>
        )}
        <div className="flex items-center gap-1 ml-auto flex-shrink-0">
          {/* Cluster resolution badge (GRAPH_CLUSTER_RESOLUTION) */}
          {d.clusterGroupId && (
            <button
              onClick={(e) => { e.stopPropagation(); d.onResolveCluster?.(); }}
              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors"
              title="Cluster candidate — click to resolve"
            >
              <AlertOctagon className="h-2.5 w-2.5" />
            </button>
          )}
          {cfg.showConnectionCount && d.connectionCount > 0 && (
            <span className="text-[9px] font-bold text-[var(--text-sec)] bg-[var(--slate-dim)] rounded-full px-1.5 py-0.5 leading-none border border-[var(--slate)]">
              {d.connectionCount}
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <Link
        to={`/prioritization/items/${d.id}`}
        className="text-xs font-semibold text-[var(--text-pri)] line-clamp-2 hover:text-[var(--text-ter)] transition-colors leading-snug block"
        onClick={(e) => e.stopPropagation()}
      >
        {d.label}
      </Link>

      {/* Altitude */}
      {cfg.showAltitude && d.altitude && (
        <div className="mt-1">
          <span className="text-[9px] text-[var(--text-ter)]">L{d.altitude}</span>
        </div>
      )}
    </div>
  );
}

const nodeTypes: NodeTypes = { item: ItemNode };

// ============================================================================
// Custom Edge — standard labeled edge
// ============================================================================

function LabeledEdge({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition,
  style, markerEnd, data, selected,
}: EdgeProps) {
  const { deleteElements } = useReactFlow();
  const [edgePath] = getSmoothStepPath({
    sourceX, sourceY, sourcePosition,
    targetX, targetY, targetPosition,
    borderRadius: 8,
  });

  const color      = (data as any)?.color      as string ?? '#94a3b8';
  const label      = (data as any)?.label      as string | undefined;
  const EdgeIcon   = (data as any)?.icon       as React.ComponentType<{ className?: string }> | undefined;
  const colorLight = (data as any)?.colorLight as string ?? '#f8fafc';

  const lx = sourceX + 0.75 * (targetX - sourceX);
  const ly = sourceY + 0.75 * (targetY - sourceY);

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          strokeWidth: selected
            ? Math.max(Number((style as { strokeWidth?: number })?.strokeWidth ?? 1.5) + 1, 2.5)
            : (style as { strokeWidth?: number })?.strokeWidth,
        }}
        markerEnd={markerEnd}
        interactionWidth={24}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${lx}px,${ly}px)`,
              pointerEvents: 'all',
              zIndex: 10,
            }}
          >
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border leading-none whitespace-nowrap shadow-sm ${
                selected ? 'ring-2 ring-offset-1 ring-indigo-400' : ''
              }`}
              style={{ backgroundColor: colorLight, color, borderColor: `${color}40` }}
              title="Click edge, then press Delete — or use Remove"
            >
              {EdgeIcon && <EdgeIcon className="h-2.5 w-2.5 flex-shrink-0" />}
              {label}
              {selected && (
                <button
                  type="button"
                  aria-label="Remove relationship"
                  title="Remove relationship"
                  className="ml-0.5 inline-flex items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-[var(--bg-raised)]/10 p-0.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    void deleteElements({ edges: [{ id }] });
                  }}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

// ============================================================================
// Ghost Edge — Nexus proposals (GRAPH_NEXUS_OVERLAY)
// ============================================================================

function GhostEdge({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition, data,
}: EdgeProps) {
  const [edgePath] = getSmoothStepPath({
    sourceX, sourceY, sourcePosition,
    targetX, targetY, targetPosition,
    borderRadius: 8,
  });

  const proposal   = (data as any)?.proposal   as NexusRelationshipProposal | undefined;
  const onAccept   = (data as any)?.onAccept   as ((id: string) => void)    | undefined;
  const onDismiss  = (data as any)?.onDismiss  as ((id: string) => void)    | undefined;
  const accepting  = (data as any)?.accepting  as boolean ?? false;
  const dismissing = (data as any)?.dismissing as boolean ?? false;

  const lx = sourceX + 0.5 * (targetX - sourceX);
  const ly = sourceY + 0.5 * (targetY - sourceY);

  const confidenceColor = proposal?.confidence === 'high' ? '#10b981' : proposal?.confidence === 'medium' ? '#f59e0b' : '#94a3b8';

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: '#06b6d4',
          strokeWidth: 1.5,
          strokeDasharray: '6,4',
          opacity: 0.55,
        }}
        markerEnd={{ type: MarkerType.ArrowClosed, color: '#06b6d4', width: 16, height: 16 }}
      />
      {proposal && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${lx}px,${ly}px)`,
              pointerEvents: 'all',
              zIndex: 20,
            }}
          >
            <div className="flex items-center gap-1 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-cyan-200 dark:border-cyan-700/50 shadow-md px-2 py-1">
              <Sparkles className="h-3 w-3 text-cyan-500 flex-shrink-0" />
              <span className="text-[10px] font-semibold text-cyan-700 dark:text-cyan-300 whitespace-nowrap max-w-[120px] truncate" title={proposal.rationale}>
                {proposal.relationshipType}
              </span>
              <span
                className="text-[9px] font-medium px-1 rounded leading-none"
                style={{ backgroundColor: `${confidenceColor}20`, color: confidenceColor }}
              >
                {proposal.confidence}
              </span>
              <button
                onClick={() => onAccept?.(proposal.id)}
                disabled={accepting || dismissing}
                className="inline-flex items-center justify-center w-5 h-5 rounded bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] transition-colors disabled:opacity-40"
                title="Accept this relationship"
              >
                {accepting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              </button>
              <button
                onClick={() => onDismiss?.(proposal.id)}
                disabled={accepting || dismissing}
                className="inline-flex items-center justify-center w-5 h-5 rounded bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors disabled:opacity-40"
                title="Dismiss this proposal"
              >
                {dismissing ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              </button>
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const edgeTypes: EdgeTypes = { labeled: LabeledEdge, ghost: GhostEdge };

// ============================================================================
// Layout helpers
// ============================================================================

type LayoutMode = 'hierarchy' | 'grid';

function buildConnectionCounts(validEdges: any[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const e of validEdges) {
    counts[e.sourceItemId] = (counts[e.sourceItemId] || 0) + 1;
    counts[e.targetItemId] = (counts[e.targetItemId] || 0) + 1;
  }
  return counts;
}

// Pool-violations overlay (GRAPH_POOL_VIOLATIONS)
export interface NodeViolation { disposition: 'block' | 'warn'; patterns: string[]; }
type NodeExtras = {
  pathDistance?: number | null;
  clusterGroupId?: string | null;
  onResolveCluster?: (() => void) | null;
  violation?: NodeViolation | null;
  poolDimmed?: boolean;
};

function buildNodeData(
  item: any,
  counts: Record<string, number>,
  cardConfig: CardConfig,
  dimmed = false,
  extras: NodeExtras = {},
) {
  return {
    label:           item.title || 'Untitled',
    category:        item.itemCategory || '',
    altitude:        item.altitudeLevel ? String(item.altitudeLevel) : null,
    id:              item.id,
    connectionCount: counts[item.id] || 0,
    dimmed,
    cardConfig,
    pathDistance:      extras.pathDistance ?? null,
    clusterGroupId:    extras.clusterGroupId ?? null,
    onResolveCluster:  extras.onResolveCluster ?? null,
    violation:         extras.violation ?? null,
    poolDimmed:        extras.poolDimmed ?? false,
  };
}

function buildFlowEdges(edges: any[]): Edge[] {
  return edges.map((rel: any) => {
    const type = (rel.type || 'related') as string;
    const cfg  = EDGE_CONFIG[type] || EDGE_CONFIG.related;
    // Always label — unlabeled dashed "Related" edges were impossible to select/remove.
    const labelText =
      type === 'carries' && typeof rel.magnitude === 'number'
        ? `Carries ${formatCarriesShare(rel.magnitude)}`
        : rel.subtype || cfg.label;
    const groupId =
      typeof rel._groupId === 'string'
        ? rel._groupId
        : typeof rel.id === 'string' && rel.id.startsWith('group-edge-')
          ? rel.id.slice('group-edge-'.length, 'group-edge-'.length + 36)
          : undefined;

    return {
      id: `e-${rel.id || `${rel.sourceItemId}-${rel.targetItemId}`}`,
      source: rel.sourceItemId,
      target: rel.targetItemId,
      type: 'labeled',
      selectable: true,
      deletable: true,
      focusable: true,
      markerEnd: cfg.directed ? { type: MarkerType.ArrowClosed, color: cfg.color } : undefined,
      style: {
        stroke: cfg.color,
        strokeWidth: cfg.directed ? 2 : 1.5,
        strokeDasharray: cfg.dashed ? '5,4' : undefined,
        opacity: 1,
      },
      data: {
        label: labelText,
        color: cfg.color,
        colorLight: cfg.colorLight,
        icon: cfg.icon,
        relType: type,
        relationshipId: rel.id as string | undefined,
        groupId,
      },
    };
  });
}

function buildDagreLayout(
  items: any[],
  edges: any[],
  cardConfig: CardConfig,
  extras: Map<string, NodeExtras>,
): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 80, ranksep: 100 });
  g.setDefaultEdgeLabel(() => ({}));

  for (const item of items) g.setNode(item.id, { width: NODE_WIDTH, height: NODE_HEIGHT });

  const idSet = new Set(items.map((i: any) => i.id));
  const valid = (edges || []).filter((e: any) => idSet.has(e.sourceItemId) && idSet.has(e.targetItemId));

  for (const e of valid) g.setEdge(e.sourceItemId, e.targetItemId);
  dagre.layout(g);

  const counts = buildConnectionCounts(valid);
  const nodes: Node[] = items.map((item: any) => {
    const pos = g.node(item.id);
    return {
      id: item.id, type: 'item',
      position: pos ? { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 } : { x: 0, y: 0 },
      data: buildNodeData(item, counts, cardConfig, false, extras.get(item.id) ?? {}),
    };
  });
  return { nodes, edges: buildFlowEdges(valid) };
}

function buildGridLayout(
  items: any[],
  edges: any[],
  cardConfig: CardConfig,
  extras: Map<string, NodeExtras>,
): { nodes: Node[]; edges: Edge[] } {
  const cols = Math.ceil(Math.sqrt(Math.max(items.length, 1)));
  const idSet = new Set(items.map((i: any) => i.id));
  const valid = (edges || []).filter((e: any) => idSet.has(e.sourceItemId) && idSet.has(e.targetItemId));
  const counts = buildConnectionCounts(valid);

  const nodes: Node[] = items.map((item: any, i: number) => ({
    id: item.id, type: 'item',
    position: { x: (i % cols) * 260, y: Math.floor(i / cols) * 150 },
    data: buildNodeData(item, counts, cardConfig, false, extras.get(item.id) ?? {}),
  }));
  return { nodes, edges: buildFlowEdges(valid) };
}

function layoutNodes(
  items: any[],
  edges: any[],
  mode: LayoutMode,
  cfg: CardConfig,
  extras: Map<string, NodeExtras>,
) {
  return mode === 'hierarchy'
    ? buildDagreLayout(items, edges, cfg, extras)
    : buildGridLayout(items, edges, cfg, extras);
}

// ============================================================================
// BFS reachability (GRAPH_PATH_ANALYSIS)
// ============================================================================

interface ReachResult {
  /** node id → hop distance from source. source itself = 0 */
  distances: Map<string, number>;
  /** ids of all upstream nodes (following edges reversed) */
  upstream: Set<string>;
  /** ids of all downstream nodes (following edges forward) */
  downstream: Set<string>;
}

function computeReachability(sourceId: string, edges: any[]): ReachResult {
  // Build adjacency lists (bidirectional for BFS)
  const fwd = new Map<string, string[]>(); // source → targets
  const bwd = new Map<string, string[]>(); // target → sources

  for (const e of edges) {
    if (!fwd.has(e.sourceItemId)) fwd.set(e.sourceItemId, []);
    fwd.get(e.sourceItemId)!.push(e.targetItemId);
    if (!bwd.has(e.targetItemId)) bwd.set(e.targetItemId, []);
    bwd.get(e.targetItemId)!.push(e.sourceItemId);
  }

  // BFS downstream (items that depend on source)
  const downstream = new Set<string>();
  const downQueue = [sourceId];
  while (downQueue.length > 0) {
    const cur = downQueue.shift()!;
    for (const next of fwd.get(cur) ?? []) {
      if (!downstream.has(next) && next !== sourceId) {
        downstream.add(next);
        downQueue.push(next);
      }
    }
  }

  // BFS upstream (items source depends on)
  const upstream = new Set<string>();
  const upQueue = [sourceId];
  while (upQueue.length > 0) {
    const cur = upQueue.shift()!;
    for (const prev of bwd.get(cur) ?? []) {
      if (!upstream.has(prev) && prev !== sourceId) {
        upstream.add(prev);
        upQueue.push(prev);
      }
    }
  }

  // BFS distance for all reachable (undirected) — for color intensity
  const distances = new Map<string, number>();
  distances.set(sourceId, 0);
  const bfsQueue: Array<{ id: string; dist: number }> = [{ id: sourceId, dist: 0 }];
  while (bfsQueue.length > 0) {
    const { id: cur, dist } = bfsQueue.shift()!;
    const neighbors = [...(fwd.get(cur) ?? []), ...(bwd.get(cur) ?? [])];
    for (const n of neighbors) {
      if (!distances.has(n)) {
        distances.set(n, dist + 1);
        bfsQueue.push({ id: n, dist: dist + 1 });
      }
    }
  }

  return { distances, upstream, downstream };
}

// ============================================================================
// Connection type picker — appears after dragging an arrow
// ============================================================================

interface PendingConnection {
  source: string;
  target: string;
}

function ConnectionTypePicker({
  pending,
  onSelect,
  onCancel,
}: {
  pending: PendingConnection;
  onSelect: (type: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState<string | null>(null);
  const { isDark } = useTheme();

  const handleSelect = async (type: string) => {
    setSaving(type);
    try { await onSelect(type); } finally { setSaving(null); }
  };

  return (
    <div
      className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-xl p-4"
      style={{ minWidth: 260 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Choose relationship type</p>
        <button onClick={onCancel} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-1 max-h-[min(60vh,420px)] overflow-y-auto">
        {UI_EDGE_TYPE_ORDER.map((type) => {
          const cfg = UI_EDGE_CONFIG[type];
          const Icon = cfg.icon;
          const isSaving = saving === type;
          const pillBg = isDark ? `${cfg.color}22` : cfg.colorLight;
          return (
            <button
              key={type}
              onClick={() => void handleSelect(cfg.writeType)}
              disabled={saving !== null}
              className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors text-left disabled:opacity-50"
            >
              <span
                className="inline-flex items-center justify-center rounded-full p-1.5 flex-shrink-0 mt-0.5 border"
                style={{
                  backgroundColor: pillBg,
                  borderColor: `${cfg.color}${isDark ? '55' : '35'}`,
                  color: cfg.color,
                }}
              >
                {isSaving
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Icon className="h-3.5 w-3.5" />}
              </span>
              <div>
                <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{cfg.label}</p>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{cfg.description}</p>
              </div>
              {cfg.directed && (
                <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-dim)] ml-auto mt-1 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Legend Panel
// ============================================================================

function LegendPanel() {
  const [open, setOpen] = useState(true);
  const { isDark } = useTheme();

  return (
    <div
      className="border shadow-sm text-xs overflow-hidden"
      style={{
        minWidth: 170,
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        borderColor: isDark ? '#334155' : '#e5e7eb',
      }}
    >
      <button
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-1.5 px-3 py-2 w-full text-left font-semibold transition-colors ${isDark ? 'text-[var(--text-ter)] hover:bg-[var(--bg-raised)]/50' : 'text-[var(--text-sec)] hover:bg-[var(--surface)]'}`}
      >
        Legend
        {open ? <ChevronDown className="h-3 w-3 ml-auto" /> : <ChevronUp className="h-3 w-3 ml-auto" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-1 border-t" style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
          <p className={`text-[9px] uppercase tracking-wide mt-2 mb-1.5 font-semibold ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
            Relationship type
          </p>
          {UI_EDGE_TYPE_ORDER.map(key => {
            const cfg = UI_EDGE_CONFIG[key];
            const pillBg = isDark ? `${cfg.color}22` : cfg.colorLight;
            return (
              <div key={key} className="flex items-center gap-1.5" title={`${cfg.label}: ${cfg.description}`}>
                <span
                  className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 border font-medium leading-none"
                  style={{
                    backgroundColor: pillBg,
                    color: cfg.color,
                    borderColor: `${cfg.color}${isDark ? '55' : '40'}`,
                    fontSize: 9,
                  }}
                >
                  <cfg.icon className="h-2.5 w-2.5" />
                  {cfg.label}
                </span>
                {cfg.dashed && <span className={`text-[9px] ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>dashed</span>}
                {cfg.directed && <ArrowRight className="h-2.5 w-2.5 text-[var(--text-ter)]" />}
              </div>
            );
          })}
          <p className={`text-[9px] mt-2 ${isDark ? 'text-[var(--text-sec)]' : 'text-[var(--text-ter)]'}`}>
            Drag between nodes to add a relationship
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Card Config Panel
// ============================================================================

function CardConfigPanel({
  config,
  onChange,
}: {
  config: CardConfig;
  onChange: (c: CardConfig) => void;
}) {
  const { isDark } = useTheme();

  const fields: Array<{ key: keyof CardConfig; label: string; description: string }> = [
    { key: 'showCategory',        label: 'Category',         description: 'Item type label (Problem, Opportunity, etc.)' },
    { key: 'showAltitude',        label: 'Altitude level',   description: 'Numeric altitude (L1–L5)' },
    { key: 'showConnectionCount', label: 'Connection count', description: 'Number of relationships on this item' },
  ];

  return (
    <div
      className="border shadow-sm text-xs overflow-hidden"
      style={{
        minWidth: 220,
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        borderColor: isDark ? '#334155' : '#e5e7eb',
      }}
    >
      <div className={`flex items-center gap-1.5 px-3 py-2 border-b font-semibold ${isDark ? 'text-[var(--text-ter)] border-[var(--rule)]' : 'text-[var(--text-sec)] border-[var(--rule)]'}`}>
        <Settings2 className="h-3 w-3" />
        Card fields
      </div>
      <div className="px-3 py-2.5 space-y-2.5">
        {fields.map(({ key, label, description }) => (
          <label key={key} className="flex items-start gap-2.5 cursor-pointer">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                checked={config[key]}
                onChange={(e) => onChange({ ...config, [key]: e.target.checked })}
                className="sr-only"
              />
              <div
                className="w-4 h-4 rounded border-2 flex items-center justify-center transition-colors"
                style={{
                  borderColor: config[key] ? '#6366f1' : (isDark ? '#475569' : '#d1d5db'),
                  backgroundColor: config[key] ? '#6366f1' : 'transparent',
                }}
              >
                {config[key] && <Check className="h-2.5 w-2.5 text-white" />}
              </div>
            </div>
            <div>
              <p className={`font-medium leading-none ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>{label}</p>
              <p className={`mt-0.5 leading-tight ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>{description}</p>
            </div>
          </label>
        ))}
        <p className={`text-[9px] mt-1 pt-2 border-t ${isDark ? 'text-[var(--text-sec)] border-[var(--rule)]' : 'text-[var(--text-ter)] border-[var(--rule)]'}`}>
          Title and item link always shown
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Item Inspector Panel (GRAPH_INSPECTOR)
// ============================================================================

function ItemInspectorPanel({
  itemId,
  allItems,
  edges,
  groups,
  groupMemberships,
  lineageEvents,
  onClose,
  presentation = 'sidebar',
}: {
  itemId: string;
  allItems: any[];
  edges: any[];
  groups: ItemRelationshipGroup[];
  groupMemberships: ItemRelationshipGroupMembership[];
  lineageEvents: any[];
  onClose: () => void;
  presentation?: 'sidebar' | 'sheet';
}) {
  const { isDark } = useTheme();

  const itemMap = useMemo(() => new Map(allItems.map((i: any) => [i.id, i])), [allItems]);

  const itemEdges = useMemo(
    () => edges.filter((e: any) => e.sourceItemId === itemId || e.targetItemId === itemId),
    [edges, itemId],
  );

  const itemGroups = useMemo(() => {
    const membershipGroupIds = new Set(
      groupMemberships.filter((m) => m.itemId === itemId).map((m) => m.groupId),
    );
    return groups.filter((g) => membershipGroupIds.has(g.id));
  }, [groups, groupMemberships, itemId]);

  const itemLineage = useMemo(
    () => lineageEvents.filter((e: any) => e.itemId === itemId || e.sourceItemId === itemId),
    [lineageEvents, itemId],
  );

  const item = allItems.find((i: any) => i.id === itemId);
  if (!item) return null;

  const bg = isDark ? '#1e293b' : '#ffffff';
  const border = isDark ? '#334155' : '#e5e7eb';
  const textPri = isDark ? '#f1f5f9' : '#111827';
  const textSec = isDark ? '#94a3b8' : '#6b7280';
  const textTer = isDark ? '#64748b' : '#9ca3af';

  return (
    <>
      {presentation === 'sheet' && (
        <button
          type="button"
          className="absolute inset-0 z-40 bg-black/40"
          onClick={onClose}
          aria-label="Close inspector"
        />
      )}
      <div
        className={
          presentation === 'sheet'
            ? 'absolute bottom-0 left-0 right-0 max-h-[75vh] overflow-y-auto shadow-2xl z-50 flex flex-col '
            : 'absolute top-0 right-0 h-full overflow-y-auto shadow-xl z-30 flex flex-col'
        }
        style={
          presentation === 'sheet'
            ? { backgroundColor: bg, borderTop: `1px solid ${border}` }
            : { width: 280, backgroundColor: bg, borderLeft: `1px solid ${border}` }
        }
      >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-3" style={{ borderBottom: `1px solid ${border}` }}>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: textTer }}>
            {item.itemCategory || 'Item'}
          </p>
          <p className="text-sm font-semibold leading-snug" style={{ color: textPri }}>{item.title || 'Untitled'}</p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 mt-0.5 p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--bg-raised)]/50 text-[var(--text-ter)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 px-4 py-3 space-y-4 text-xs">
        {/* Quick link */}
        <Link
          to={`/prioritization/items/${itemId}`}
          className="flex items-center gap-1.5 font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline"
        >
          <Eye className="h-3.5 w-3.5" />
          Open item detail
          <ChevronRight className="h-3 w-3 ml-auto" />
        </Link>

        {/* Relationships */}
        <div>
          <p className="font-semibold mb-2 flex items-center gap-1.5" style={{ color: textSec }}>
            <Network className="h-3.5 w-3.5" />
            Relationships ({itemEdges.length})
          </p>
          {itemEdges.length === 0 ? (
            <p className="italic" style={{ color: textTer }}>No relationships yet</p>
          ) : (
            <div className="space-y-1.5">
              {itemEdges.map((edge: any) => {
                const isSource = edge.sourceItemId === itemId;
                const partnerId = isSource ? edge.targetItemId : edge.sourceItemId;
                const partner = itemMap.get(partnerId);
                const cfg = EDGE_CONFIG[edge.type] || EDGE_CONFIG.related;
                const Icon = cfg.icon;
                return (
                  <div key={edge.id} className="flex items-start gap-2 p-2" style={{ backgroundColor: isDark ? '#0f172a' : '#f8fafc' }}>
                    <span
                      className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full border mt-0.5"
                      style={{ backgroundColor: `${cfg.color}18`, borderColor: `${cfg.color}40`, color: cfg.color }}
                    >
                      <Icon className="h-3 w-3" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate" style={{ color: textPri }} title={partner?.title}>
                        {partner?.title || partnerId.slice(0, 8) + '…'}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span style={{ color: cfg.color }}>{cfg.label}</span>
                        {edge.directionality === 'directed' && (
                          <span style={{ color: textTer }}>{isSource ? '→' : '←'}</span>
                        )}
                        {edge.status && edge.status !== 'active' && (
                          <span className="px-1 rounded text-[9px] font-medium" style={{ backgroundColor: isDark ? '#1e293b' : '#f1f5f9', color: textTer }}>
                            {edge.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Groups */}
        {itemGroups.length > 0 && (
          <div>
            <p className="font-semibold mb-2 flex items-center gap-1.5" style={{ color: textSec }}>
              <Users className="h-3.5 w-3.5" />
              Groups ({itemGroups.length})
            </p>
            <div className="space-y-1.5">
              {itemGroups.map((g) => {
                const color =
                  EDGE_CONFIG[g.type]?.color
                  ?? (g.type === 'cluster' ? '#06b6d4' : g.type === 'package' ? '#f97316' : g.type === 'exclusive' ? '#f43f5e' : '#8b5cf6');
                return (
                  <div key={g.id} className="flex items-center gap-2 p-2" style={{ backgroundColor: isDark ? '#0f172a' : '#f8fafc' }}>
                    <span className="flex-shrink-0 w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                    <div className="min-w-0">
                      <p className="font-medium truncate" style={{ color: textPri }}>{g.title || g.type}</p>
                      <p style={{ color: textTer }}>{g.type}{g.subtype ? ` · ${g.subtype}` : ''} · {g.status}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Lineage */}
        {itemLineage.length > 0 && (
          <div>
            <p className="font-semibold mb-2 flex items-center gap-1.5" style={{ color: textSec }}>
              <Zap className="h-3.5 w-3.5" />
              Lineage events ({itemLineage.length})
            </p>
            <div className="space-y-1.5">
              {itemLineage.map((ev: any, i: number) => (
                <div key={ev.id ?? i} className="p-2" style={{ backgroundColor: isDark ? '#0f172a' : '#f8fafc' }}>
                  <p className="font-medium" style={{ color: textPri }}>{ev.eventType || ev.lineageEventType || 'Lineage event'}</p>
                  {ev.createdAt && (
                    <p style={{ color: textTer }}>{new Date(ev.createdAt).toLocaleDateString()}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

// ============================================================================
// Cluster Resolution Modal (GRAPH_CLUSTER_RESOLUTION)
// ============================================================================

type ClusterStep = 'review' | 'action' | 'canonical' | 'confirm';

function ClusterResolutionModal({
  groupId,
  group,
  memberships,
  allItems,
  onResolve,
  onClose,
}: {
  groupId: string;
  group: ItemRelationshipGroup;
  memberships: ItemRelationshipGroupMembership[];
  allItems: any[];
  onResolve: (action: 'duplicate' | 'variant' | 'dismiss', canonicalItemId?: string) => Promise<void>;
  onClose: () => void;
}) {
  const [step, setStep] = useState<ClusterStep>('review');
  const [action, setAction] = useState<'duplicate' | 'variant' | 'dismiss' | null>(null);
  const [canonicalId, setCanonicalId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { isDark } = useTheme();

  const members = useMemo(() => {
    const memberItemIds = new Set(memberships.map((m) => m.itemId));
    return allItems.filter((i: any) => memberItemIds.has(i.id));
  }, [memberships, allItems]);

  const handleConfirm = async () => {
    if (!action) return;
    if (action === 'duplicate' && !canonicalId) return;
    setSaving(true);
    try {
      await onResolve(action, canonicalId ?? undefined);
    } finally {
      setSaving(false);
    }
  };

  const bg = isDark ? '#1e293b' : '#ffffff';
  const border = isDark ? '#334155' : '#e5e7eb';
  const textPri = isDark ? '#f1f5f9' : '#111827';
  const textSec = isDark ? '#94a3b8' : '#6b7280';

  const stepTitles: Record<ClusterStep, string> = {
    review:    '1. Review cluster members',
    action:    '2. Choose resolution',
    canonical: '3. Pick canonical item',
    confirm:   '4. Confirm',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div
        className="shadow-2xl flex flex-col"
        style={{ width: 480, maxHeight: '80vh', backgroundColor: bg, border: `1px solid ${border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${border}` }}>
          <div>
            <div className="flex items-center gap-2">
              <AlertOctagon className="h-4 w-4 text-[var(--mustard)]" />
              <p className="text-sm font-semibold" style={{ color: textPri }}>Resolve Cluster</p>
            </div>
            <p className="text-xs mt-0.5" style={{ color: textSec }}>{stepTitles[step]}</p>
          </div>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 text-xs">
          {/* Step: review */}
          {step === 'review' && (
            <div className="space-y-3">
              <p style={{ color: textSec }}>
                These {members.length} items are in a candidate cluster{group.title ? ` "${group.title}"` : ''}.
                Review them and decide how to resolve the relationship.
              </p>
              <div className="space-y-2">
                {members.map((item: any) => (
                  <div key={item.id} className="flex items-start gap-2 p-3" style={{ backgroundColor: isDark ? '#0f172a' : '#f8fafc', border: `1px solid ${border}` }}>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm" style={{ color: textPri }}>{item.title || 'Untitled'}</p>
                      {item.itemCategory && <p style={{ color: textSec }} className="mt-0.5">{item.itemCategory}</p>}
                    </div>
                    <Link to={`/prioritization/items/${item.id}`} className="flex-shrink-0 text-[var(--text-ter)] hover:text-[var(--text-ter)]" onClick={onClose}>
                      <Eye className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step: action */}
          {step === 'action' && (
            <div className="space-y-2">
              <p className="mb-3" style={{ color: textSec }}>How should this cluster be resolved?</p>
              {[
                { value: 'duplicate' as const, label: 'Mark as duplicates', desc: 'These items describe the same thing. One will be canonical; the rest closed.' },
                { value: 'variant' as const,   label: 'Mark as variants',   desc: 'These items are meaningfully different forms of the same concept. All are kept.' },
                { value: 'dismiss' as const,   label: 'Dismiss cluster',    desc: 'These items are not actually related. Remove the cluster relationship.' },
              ].map(({ value, label, desc }) => (
                <button
                  key={value}
                  onClick={() => setAction(value)}
                  className={`w-full text-left  p-3 border transition-colors ${action === value ? 'border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30' : ''}`}
                  style={{ borderColor: action === value ? '#6366f1' : border }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3.5 h-3.5 rounded-full border-2 flex-shrink-0"
                      style={{ borderColor: action === value ? '#6366f1' : '#9ca3af', backgroundColor: action === value ? '#6366f1' : 'transparent' }}
                    />
                    <p className="font-semibold" style={{ color: textPri }}>{label}</p>
                  </div>
                  <p className="mt-1 pl-5.5" style={{ color: textSec }}>{desc}</p>
                </button>
              ))}
            </div>
          )}

          {/* Step: canonical */}
          {step === 'canonical' && (
            <div className="space-y-2">
              <p className="mb-3" style={{ color: textSec }}>Which item is the canonical (authoritative) version?</p>
              {members.map((item: any) => (
                <button
                  key={item.id}
                  onClick={() => setCanonicalId(item.id)}
                  className="w-full text-left p-3 border transition-colors"
                  style={{ borderColor: canonicalId === item.id ? '#6366f1' : border, backgroundColor: canonicalId === item.id ? (isDark ? '#1e1b4b' : '#eef2ff') : 'transparent' }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3.5 h-3.5 rounded-full border-2 flex-shrink-0"
                      style={{ borderColor: canonicalId === item.id ? '#6366f1' : '#9ca3af', backgroundColor: canonicalId === item.id ? '#6366f1' : 'transparent' }}
                    />
                    <p className="font-semibold text-sm" style={{ color: textPri }}>{item.title || 'Untitled'}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step: confirm */}
          {step === 'confirm' && (
            <div className="space-y-3">
              <div className="p-3" style={{ backgroundColor: isDark ? '#0f172a' : '#f8fafc', border: `1px solid ${border}` }}>
                <p className="font-semibold mb-2" style={{ color: textPri }}>Summary</p>
                <p style={{ color: textSec }}>Action: <span className="font-medium" style={{ color: textPri }}>{action === 'duplicate' ? 'Mark as duplicates' : action === 'variant' ? 'Mark as variants' : 'Dismiss cluster'}</span></p>
                <p style={{ color: textSec }}>Members: <span className="font-medium" style={{ color: textPri }}>{members.length} items</span></p>
                {action === 'duplicate' && canonicalId && (
                  <p style={{ color: textSec }}>Canonical: <span className="font-medium" style={{ color: textPri }}>{allItems.find((i: any) => i.id === canonicalId)?.title || canonicalId}</span></p>
                )}
              </div>
              <p className="text-[11px]" style={{ color: textSec }}>This will update the cluster group status. The action can be reviewed in Relationship Health.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderTop: `1px solid ${border}` }}>
          <button
            onClick={() => {
              if (step === 'review') onClose();
              else if (step === 'action') setStep('review');
              else if (step === 'canonical') setStep('action');
              else if (step === 'confirm') setStep(action === 'duplicate' ? 'canonical' : 'action');
            }}
            className="text-xs font-medium px-3 py-1.5 border transition-colors hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/50"
            style={{ borderColor: border, color: textSec }}
          >
            {step === 'review' ? 'Cancel' : 'Back'}
          </button>

          {step === 'confirm' ? (
            <button
              onClick={() => void handleConfirm()}
              disabled={saving}
              className="text-xs font-semibold px-4 py-1.5 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Confirm resolution
            </button>
          ) : (
            <button
              onClick={() => {
                if (step === 'review') setStep('action');
                else if (step === 'action') {
                  if (!action) return;
                  setStep(action === 'duplicate' ? 'canonical' : 'confirm');
                } else if (step === 'canonical') {
                  if (!canonicalId) return;
                  setStep('confirm');
                }
              }}
              disabled={(step === 'action' && !action) || (step === 'canonical' && !canonicalId)}
              className="text-xs font-semibold px-4 py-1.5 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Graph inner
// ============================================================================

interface GraphInnerProps {
  filteredItems:     any[];
  filteredEdges:     any[];
  layoutMode:        LayoutMode;
  totalItemCount:    number;
  cardConfig:        CardConfig;
  onNewEdge:         (conn: PendingConnection) => void;
  onRemoveEdges:     (edges: Edge[]) => Promise<void>;
  onFocusChange?:    (nodeId: string | null) => void;
  nodeExtras:        Map<string, NodeExtras>;
  nexusEdges:        Edge[];
  pathAnalysisMode:  boolean;
  pathReachResult:   ReachResult | null;
}

function GraphInner({
  filteredItems, filteredEdges, layoutMode, totalItemCount, cardConfig,
  onNewEdge, onRemoveEdges, onFocusChange, nodeExtras, nexusEdges, pathAnalysisMode, pathReachResult,
}: GraphInnerProps) {
  const { fitView } = useReactFlow();
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => layoutNodes(filteredItems, filteredEdges, layoutMode, cardConfig, nodeExtras),
    [filteredItems, filteredEdges, layoutMode, cardConfig, nodeExtras],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([...initialEdges, ...nexusEdges]);

  // Re-sync when filters/layout/nexus change
  useEffect(() => {
    setNodes(prev => {
      if (prev.length !== initialNodes.length) return initialNodes;
      return initialNodes;
    });
    setEdges([...initialEdges, ...nexusEdges]);
    setTimeout(() => fitView({ padding: 0.2 }), 50);
  }, [initialNodes, initialEdges, nexusEdges, setNodes, setEdges, fitView]);

  // Update card config without re-layout
  useEffect(() => {
    setNodes(prev => prev.map(n => ({ ...n, data: { ...n.data, cardConfig } })));
  }, [cardConfig, setNodes]);

  // Highlighting: ego-network (1-hop) or transitive (BFS)
  const neighborIds = useMemo(() => {
    if (!focusedNodeId) return null;

    if (pathAnalysisMode && pathReachResult) {
      // Return the full reachable set
      return new Set([focusedNodeId, ...pathReachResult.upstream, ...pathReachResult.downstream]);
    }

    // Default: 1-hop ego-network
    const ids = new Set([focusedNodeId]);
    for (const e of initialEdges) {
      if (e.source === focusedNodeId) ids.add(e.target);
      if (e.target === focusedNodeId) ids.add(e.source);
    }
    return ids;
  }, [focusedNodeId, initialEdges, pathAnalysisMode, pathReachResult]);

  const displayNodes = useMemo(() =>
    nodes.map(n => {
      const pathDistance = (pathAnalysisMode && pathReachResult)
        ? (pathReachResult.distances.get(n.id) ?? null)
        : null;
      return {
        ...n,
        data: {
          ...n.data,
          dimmed: neighborIds ? !neighborIds.has(n.id) : false,
          pathDistance,
        },
      };
    }),
  [nodes, neighborIds, pathAnalysisMode, pathReachResult]);

  const displayEdges = useMemo(() =>
    neighborIds
      ? edges.map(e => ({ ...e, style: { ...e.style, opacity: (neighborIds.has(e.source) && neighborIds.has(e.target)) ? 1 : 0.07 } }))
      : edges,
  [edges, neighborIds]);

  const onConnect = useCallback((conn: Connection) => {
    if (!conn.source || !conn.target || conn.source === conn.target) return;
    onNewEdge({ source: conn.source, target: conn.target });
  }, [onNewEdge]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    const nextId = focusedNodeId === node.id ? null : node.id;
    setFocusedNodeId(nextId);
    onFocusChange?.(nextId);
  }, [focusedNodeId, onFocusChange]);

  const onPaneClick = useCallback(() => {
    setFocusedNodeId(null);
    onFocusChange?.(null);
  }, [onFocusChange]);

  const onEdgesDelete = useCallback((deleted: Edge[]) => {
    void onRemoveEdges(deleted);
  }, [onRemoveEdges]);

  const reachCount = pathAnalysisMode && pathReachResult && focusedNodeId
    ? (pathReachResult.upstream.size + pathReachResult.downstream.size)
    : null;

  return (
    <ReactFlow
      nodes={displayNodes}
      edges={displayEdges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onNodeClick={onNodeClick}
      onPaneClick={onPaneClick}
      onEdgesDelete={onEdgesDelete}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      minZoom={0.1}
      maxZoom={2.5}
      connectOnClick={false}
      edgesFocusable
      edgesReconnectable={false}
      deleteKeyCode={['Backspace', 'Delete']}
      connectionLineStyle={{ stroke: '#6366f1', strokeWidth: 2, strokeDasharray: '5,4' }}
    >
      <Background gap={20} color="#f1f5f9" />
      <Controls />
      <MiniMap
        nodeColor={() => '#94a3b8'}
        maskColor="rgba(241,245,249,0.7)"
      />

      <Panel position="bottom-left">
        <LegendPanel />
      </Panel>

      {totalItemCount > ITEM_CAP && (
        <Panel position="top-center">
          <div className="flex items-center gap-1.5 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] text-xs px-3 py-1.5 shadow-sm">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            Showing {ITEM_CAP} of {totalItemCount} — use filters to narrow down
          </div>
        </Panel>
      )}

      {focusedNodeId && !pathAnalysisMode && (
        <Panel position="top-right">
          <div className="bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)] text-xs px-3 py-1.5 shadow-sm">
            Showing neighbors · click background to reset
          </div>
        </Panel>
      )}

      <Panel position="bottom-center">
        <div className="bg-[var(--bg-raised)]/90 dark:bg-[var(--bg-raised)]/90 border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-ter)] dark:text-[var(--text-ter)] text-[11px] px-3 py-1.5 shadow-sm">
          Click a relationship to select · Delete / Backspace (or ×) to remove
        </div>
      </Panel>

      {focusedNodeId && pathAnalysisMode && (
        <Panel position="top-right">
          <div className="flex items-center gap-2 bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)] text-xs px-3 py-1.5 shadow-sm">
            <Zap className="h-3.5 w-3.5" />
            {reachCount !== null
              ? `${reachCount} reachable · ${pathReachResult!.upstream.size} upstream · ${pathReachResult!.downstream.size} downstream`
              : 'No connections from this node'}
            <button onClick={() => { setFocusedNodeId(null); onFocusChange?.(null); }} className="ml-1 opacity-60 hover:opacity-100">
              <X className="h-3 w-3" />
            </button>
          </div>
        </Panel>
      )}
    </ReactFlow>
  );
}

// ============================================================================
// Page
// ============================================================================

export default function RelationshipGraph() {
  const items              = useItemsStore(s => s.items);
  const relationshipEdges  = useItemRelationshipsStore((s: any) => s.edges || []) as ItemRelationshipEdge[];
  const groups             = useItemRelationshipsStore((s: any) => s.groups || []) as ItemRelationshipGroup[];
  const groupMemberships   = useItemRelationshipsStore((s: any) => s.groupMemberships || []) as ItemRelationshipGroupMembership[];
  const lineageEvents      = useItemRelationshipsStore((s: any) => s.lineageEvents || []);
  const fetchRelationships = useItemRelationshipsStore((s: any) => s.fetchFromSupabase);
  const createEdge         = useItemRelationshipsStore((s: any) => s.createEdge);
  const createGroup        = useItemRelationshipsStore((s: any) => s.createGroup);
  const dissolveEdge       = useItemRelationshipsStore((s: any) => s.dissolveEdge);
  const dissolveGroup      = useItemRelationshipsStore((s: any) => s.dissolveGroup);
  const updateGroup        = useItemRelationshipsStore((s: any) => s.updateGroup);

  const userId      = useUserStore((s: any) => s.userId);
  const workspaceId = getCurrentWorkspaceId();

  const nexusStore    = useNexusRelationshipStore();
  const nexusWs       = workspaceId ? (nexusStore.byWorkspace[workspaceId] ?? { proposals: [], loading: false, mutating: false, error: null }) : { proposals: [], loading: false, mutating: false, error: null };
  const nexusProposals: NexusRelationshipProposal[] = nexusWs.proposals;

  // Feature flags
  const inspectorEnabled     = useFeatureFlag(FEATURE_FLAGS.GRAPH_INSPECTOR);
  const pathAnalysisEnabled  = useFeatureFlag(FEATURE_FLAGS.GRAPH_PATH_ANALYSIS);
  const clusterResEnabled    = useFeatureFlag(FEATURE_FLAGS.GRAPH_CLUSTER_RESOLUTION);
  const nexusOverlayEnabled  = useFeatureFlag(FEATURE_FLAGS.GRAPH_NEXUS_OVERLAY);
  // Base state
  const [search,          setSearch]          = useState('');
  const [selectedTypes,   setSelectedTypes]   = useState<Set<string>>(() => new Set(UI_EDGE_TYPE_ORDER));
  const [connectedOnly,   setConnectedOnly]   = useState(false);
  const [layoutMode,      setLayoutMode]      = useState<LayoutMode>('hierarchy');
  const [cardConfig,      setCardConfig]      = useState<CardConfig>(DEFAULT_CARD_CONFIG);
  const [showCardConfig,  setShowCardConfig]  = useState(false);
  const [pendingConn,     setPendingConn]     = useState<PendingConnection | null>(null);

  // Inspector state (GRAPH_INSPECTOR)
  const [inspectorNodeId, setInspectorNodeId] = useState<string | null>(null);

  // Path analysis state (GRAPH_PATH_ANALYSIS)
  const [pathAnalysisMode,   setPathAnalysisMode]   = useState(false);
  const [pathFocusedNodeId,  setPathFocusedNodeId]  = useState<string | null>(null);

  // Cluster resolution state (GRAPH_CLUSTER_RESOLUTION)
  const [clusterGroupId, setClusterGroupId] = useState<string | null>(null);

  // Nexus proposal mutation state (GRAPH_NEXUS_OVERLAY)
  const [nexusAccepting,  setNexusAccepting]  = useState<string | null>(null);
  const [nexusDismissing, setNexusDismissing] = useState<string | null>(null);

  // Pool violations state (GRAPH_POOL_VIOLATIONS)
  const [poolOptions,       setPoolOptions]       = useState<PoolScopeOption[]>([]);
  const [selectedPoolId,    setSelectedPoolId]    = useState<string | null>(null);
  const [poolLoading,       setPoolLoading]       = useState(false);
  const [activeViolationKey, setActiveViolationKey] = useState<string | null>(null);
  const [highlightItemIds,  setHighlightItemIds]  = useState<Set<string> | null>(null);

  const { isPhone, isTablet, isDesktop } = useVisualViewportFlags();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    if (isDesktop) setFiltersExpanded(true);
  }, [isDesktop]);

  useEffect(() => {
    if (typeof fetchRelationships === 'function') fetchRelationships();

    const onFocus = () => { if (typeof fetchRelationships === 'function') fetchRelationships(); };
    const onVisibility = () => { if (!document.hidden && typeof fetchRelationships === 'function') fetchRelationships(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load Nexus proposals when overlay is enabled
  useEffect(() => {
    if (nexusOverlayEnabled && workspaceId) {
      nexusStore.fetchProposals(workspaceId);
    }
  }, [nexusOverlayEnabled, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The graph inspector is read-only and always available. The feature flag still
  // controls ranking-session enforcement, not visibility of this diagnostic tool.
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setPoolLoading(true);
    loadPoolScopeOptions(workspaceId)
      .then((opts) => {
        if (cancelled) return;
        setPoolOptions(opts);
        setSelectedPoolId((cur) => cur && opts.some((o) => o.id === cur) ? cur : null);
      })
      .catch(() => { if (!cancelled) setPoolOptions([]); })
      .finally(() => { if (!cancelled) setPoolLoading(false); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const toggleType = (type: string) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  // Only active items — no status color-coding needed
  const activeItems = useMemo(() =>
    (items as any[]).filter((i: any) => {
      const status = i.exit_status || i.exitStatus || 'active';
      return status === 'active';
    }),
  [items]);

  const filteredItems = useMemo(() => {
    let result = activeItems;
    if (search) result = result.filter((i: any) => i.title?.toLowerCase().includes(search.toLowerCase()));
    return result;
  }, [activeItems, search]);

  const visibleRelationshipEdges = useMemo(
    () => relationshipEdges.filter(
      (edge) => edge.status !== 'dissolved' && edge.status !== 'dismissed',
    ),
    [relationshipEdges],
  );

  // Include Package / Competing / Cluster / Collection as undirected pair-edges.
  const allVisibleEdges = useMemo(() => {
    const groupEdges = synthesizeGroupEdges(groups, groupMemberships).filter(
      (edge) => edge.status !== 'dissolved' && edge.status !== 'dismissed',
    );
    return [...visibleRelationshipEdges, ...groupEdges];
  }, [visibleRelationshipEdges, groups, groupMemberships]);

  const relationshipTypeCounts = useMemo(() => {
    const activeItemIds = new Set(activeItems.map((item) => item.id));
    const counts: Record<string, number> = {};
    for (const edge of allVisibleEdges) {
      if (!activeItemIds.has(edge.sourceItemId) || !activeItemIds.has(edge.targetItemId)) continue;
      const type = uiTypeForStoredType(edge.type);
      counts[type] = (counts[type] ?? 0) + 1;
    }
    return counts;
  }, [activeItems, allVisibleEdges]);

  const filteredEdges = useMemo(() => {
    const idSet = new Set(filteredItems.map((i: any) => i.id));
    let edges = allVisibleEdges.filter(
      (e) => idSet.has(e.sourceItemId) && idSet.has(e.targetItemId),
    );
    edges = edges.filter((e) => selectedTypes.has(uiTypeForStoredType(e.type)));
    return edges;
  }, [filteredItems, allVisibleEdges, selectedTypes]);

  const displayItems = useMemo(() => {
    let result = filteredItems;
    if (connectedOnly) {
      const connectedIds = new Set<string>();
      for (const e of filteredEdges) { connectedIds.add(e.sourceItemId); connectedIds.add(e.targetItemId); }
      result = result.filter((i: any) => connectedIds.has(i.id));
    }
    return result.slice(0, ITEM_CAP);
  }, [filteredItems, filteredEdges, connectedOnly]);

  const displayEdges = useMemo(() => {
    const idSet = new Set(displayItems.map((i: any) => i.id));
    return filteredEdges.filter((e: any) => idSet.has(e.sourceItemId) && idSet.has(e.targetItemId));
  }, [displayItems, filteredEdges]);

  // Cluster data for the resolution feature
  const clusterCandidateMap = useMemo((): Map<string, string> => {
    if (!clusterResEnabled) return new Map();
    const map = new Map<string, string>(); // itemId → groupId
    for (const g of groups) {
      if (g.type === 'cluster' && (g.status === 'candidate' || g.status === 'confirmed')) {
        const members = groupMemberships.filter((m) => m.groupId === g.id);
        for (const m of members) {
          if (!map.has(m.itemId)) map.set(m.itemId, g.id);
        }
      }
    }
    return map;
  }, [groups, groupMemberships, clusterResEnabled]);

  // BFS reachability for path analysis
  const pathReachResult = useMemo((): ReachResult | null => {
    if (!pathAnalysisEnabled || !pathAnalysisMode || !pathFocusedNodeId) return null;
    return computeReachability(pathFocusedNodeId, displayEdges);
  }, [pathAnalysisEnabled, pathAnalysisMode, pathFocusedNodeId, displayEdges]);

  // Pool violations — detect anti-patterns within the selected pool/frame membership
  const selectedPool = useMemo(
    () => poolOptions.find((o) => o.id === selectedPoolId) ?? null,
    [poolOptions, selectedPoolId],
  );
  const violationReport = useMemo((): PoolViolationReport | null => {
    if (!selectedPool) return null;
    return computeViolations(selectedPool.itemIds, {
      items, edges: relationshipEdges, groups, memberships: groupMemberships, lineageEvents,
    });
  }, [selectedPool, items, relationshipEdges, groups, groupMemberships, lineageEvents]);
  const poolMemberSet = useMemo(
    () => (selectedPool ? new Set(selectedPool.itemIds) : null),
    [selectedPool],
  );

  // Node extras map — cluster badges + path distances + pool-violation decoration
  const nodeExtras = useMemo((): Map<string, NodeExtras> => {
    const map = new Map<string, NodeExtras>();
    for (const item of displayItems) {
      const clusterGroupId_ = clusterCandidateMap.get(item.id) ?? null;
      const v = violationReport?.perItem.get(item.id) ?? null;
      let poolDimmed = false;
      if (highlightItemIds) poolDimmed = !highlightItemIds.has(item.id);
      else if (poolMemberSet) poolDimmed = !poolMemberSet.has(item.id);
      map.set(item.id, {
        clusterGroupId: clusterGroupId_,
        onResolveCluster: clusterGroupId_ ? () => setClusterGroupId(clusterGroupId_) : null,
        pathDistance: null, // set per-render in GraphInner based on pathReachResult
        violation: v,
        poolDimmed,
      });
    }
    return map;
  }, [displayItems, clusterCandidateMap, violationReport, poolMemberSet, highlightItemIds]);

  // Nexus ghost edges
  const nexusFlowEdges = useMemo((): Edge[] => {
    if (!nexusOverlayEnabled) return [];
    const idSet = new Set(displayItems.map((i: any) => i.id));
    return nexusProposals
      .filter((p) => idSet.has(p.sourceItemId) && idSet.has(p.targetItemId))
      .map((p) => ({
        id: `nexus-${p.id}`,
        source: p.sourceItemId,
        target: p.targetItemId,
        type: 'ghost',
        data: {
          proposal: p,
          accepting:  nexusAccepting  === p.id,
          dismissing: nexusDismissing === p.id,
          onAccept:   (pid: string) => void handleNexusAccept(pid),
          onDismiss:  (pid: string) => void handleNexusDismiss(pid),
        },
        style: { pointerEvents: 'none' as const },
      }));
  }, [nexusOverlayEnabled, nexusProposals, displayItems, nexusAccepting, nexusDismissing]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFilters = search || selectedTypes.size !== UI_EDGE_TYPE_ORDER.length || connectedOnly;
  const clearFilters = () => {
    setSearch('');
    setSelectedTypes(new Set(UI_EDGE_TYPE_ORDER));
    setConnectedOnly(false);
  };

  const handleNewEdge = useCallback((conn: PendingConnection) => {
    setPendingConn(conn);
  }, []);

  const handleRemoveEdges = useCallback(async (deleted: Edge[]) => {
    for (const edge of deleted) {
      if (edge.type === 'ghost') continue;
      const data = (edge.data ?? {}) as {
        relationshipId?: string;
        groupId?: string;
      };
      if (data.groupId && typeof dissolveGroup === 'function') {
        await dissolveGroup(data.groupId);
        continue;
      }
      const relationshipId = data.relationshipId;
      if (
        relationshipId &&
        !relationshipId.startsWith('group-edge-') &&
        typeof dissolveEdge === 'function'
      ) {
        await dissolveEdge(relationshipId);
      }
    }
    if (typeof fetchRelationships === 'function') fetchRelationships();
  }, [dissolveEdge, dissolveGroup, fetchRelationships]);

  const handleEdgeTypeSelect = useCallback(async (type: string) => {
    if (!pendingConn) return;

    if (GROUP_CREATE_TYPES.has(type as RelationshipGroupType)) {
      if (typeof createGroup !== 'function') return;
      await createGroup({
        type: type as RelationshipGroupType,
        memberItemIds: [pendingConn.source, pendingConn.target],
        status: 'active',
        declaredMode: 'declared',
        createdBy: userId ?? 'unknown',
      });
    } else {
      if (typeof createEdge !== 'function') return;
      await createEdge({
        sourceItemId: pendingConn.source,
        targetItemId: pendingConn.target,
        type,
        status: 'active',
        directionality: EDGE_CONFIG[type]?.directed ? 'directed' : 'undirected',
      } as any);
    }

    setPendingConn(null);
    if (typeof fetchRelationships === 'function') fetchRelationships();
  }, [pendingConn, createEdge, createGroup, fetchRelationships, userId]);

  const handleNodeFocus = useCallback((nodeId: string | null) => {
    if (inspectorEnabled) setInspectorNodeId(nodeId);
    if (pathAnalysisEnabled && pathAnalysisMode) setPathFocusedNodeId(nodeId);
  }, [inspectorEnabled, pathAnalysisEnabled, pathAnalysisMode]);

  const handleNexusAccept = async (proposalId: string) => {
    if (!workspaceId || !userId) return;
    setNexusAccepting(proposalId);
    try {
      const proposal = nexusProposals.find((p) => p.id === proposalId);
      const result = await nexusStore.accept({ proposalId, userId, workspaceId });
      if (result.success && proposal && typeof createEdge === 'function') {
        await createEdge({
          sourceItemId: proposal.sourceItemId,
          targetItemId: proposal.targetItemId,
          type: proposal.relationshipType,
          subtype: proposal.relationshipSubtype as any,
          directionality: proposal.directionality,
          status: 'active',
          declaredMode: 'emergent',
          rationale: proposal.rationale,
        } as any);
        if (typeof fetchRelationships === 'function') fetchRelationships();
      }
    } finally {
      setNexusAccepting(null);
    }
  };

  const handleNexusDismiss = async (proposalId: string) => {
    if (!workspaceId || !userId) return;
    setNexusDismissing(proposalId);
    try {
      await nexusStore.dismiss({ proposalId, userId, workspaceId });
    } finally {
      setNexusDismissing(null);
    }
  };

  const handleClusterResolve = async (action: 'duplicate' | 'variant' | 'dismiss', canonicalItemId?: string) => {
    if (!clusterGroupId || typeof updateGroup !== 'function') return;
    const statusMap = { duplicate: 'confirmed', variant: 'confirmed', dismiss: 'dismissed' } as const;
    const subtypeMap = { duplicate: 'duplicate', variant: 'variant', dismiss: undefined } as const;
    await updateGroup(clusterGroupId, {
      status: statusMap[action],
      subtype: subtypeMap[action],
      ...(canonicalItemId ? {} : {}),
    });
    setClusterGroupId(null);
    if (typeof fetchRelationships === 'function') fetchRelationships();
  };

  const clusterGroup = clusterGroupId ? groups.find((g) => g.id === clusterGroupId) : null;
  const clusterMemberships = clusterGroupId ? groupMemberships.filter((m) => m.groupId === clusterGroupId) : [];

  const visibleNexusCount = nexusFlowEdges.length;
  const showAdvancedToolbar = isDesktop || filtersExpanded;

  if (isPhone) {
    return (
      <div className="flex flex-col h-[calc(100vh-8rem)]">
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <PageHeader
            icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />}
            iconBg="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40"
            title="Relationship Graph"
            subtitle={`${displayItems.length} items · ${displayEdges.length} relationships`}
            mb=""
            className="flex-1 min-w-0"
          />
          <button
            type="button"
            onClick={() => setFiltersExpanded((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium  border transition-colors ${
              filtersExpanded
                ? 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40 dark:border-[var(--slate)]/40'
                : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)]'
            }`}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {filtersExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>

        {filtersExpanded && (
          <div className="mb-3 space-y-2">
            <div className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1">
              {UI_EDGE_TYPE_ORDER.map((type) => {
                const cfg = UI_EDGE_CONFIG[type];
                const active = selectedTypes.has(type);
                const pill = EDGE_FILTER_PILL[cfg.filterStyle];
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleType(type)}
                    className={`inline-flex flex-shrink-0 items-center gap-1 px-2 py-1 text-[11px] rounded-full border font-semibold ${
                      active ? pill.active : pill.idle
                    }`}
                  >
                    <cfg.icon className="h-3 w-3 shrink-0" />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] cursor-pointer select-none px-1">
              <input
                type="checkbox"
                checked={connectedOnly}
                onChange={(e) => setConnectedOnly(e.target.checked)}
                className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500"
              />
              Connected only
            </label>
          </div>
        )}

        <div className="flex-1 min-h-0 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden flex flex-col">
          <RelationshipGraphListView
            items={displayItems}
            edges={displayEdges}
            edgeConfig={EDGE_CONFIG}
            uiTypeForStoredType={uiTypeForStoredType}
            search={search}
            onSearchChange={setSearch}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <PageHeader
          icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40"
          title="Relationship Graph"
          subtitle={`${displayItems.length} active items · ${displayEdges.length} relationships`}
          mb=""
          className="flex-1"
        />

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Filter className="absolute left-2.5 top-2 h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter items…"
              className="pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 w-44 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)] dark:placeholder:text-[var(--text-dim)] dark:focus:ring-primary-500/25"
            />
          </div>

          {isTablet && (
            <button
              type="button"
              onClick={() => setFiltersExpanded((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium  border transition-colors ${
                filtersExpanded
                  ? 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40 dark:border-[var(--slate)]/40'
                  : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)]'
              }`}
            >
              <Filter className="h-3.5 w-3.5" />
              Filters
              {filtersExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          )}

          {showAdvancedToolbar && (
            <>
          {/* Relationship type filter */}
          <div className="flex items-center gap-1 flex-wrap">
            {UI_EDGE_TYPE_ORDER.map(type => {
              const cfg = UI_EDGE_CONFIG[type];
              const active = selectedTypes.has(type);
              const pill = EDGE_FILTER_PILL[cfg.filterStyle];
              return (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-full border font-semibold transition-all leading-none ${
                    active ? pill.active : pill.idle
                  }`}
                  title={`${cfg.label}: ${cfg.description} ${active ? 'Click to hide these relationships.' : 'Click to show these relationships.'}`}
                  aria-pressed={active}
                >
                  <cfg.icon className="h-3 w-3 shrink-0" />
                  {cfg.label}
                  <span className="font-normal opacity-70">{relationshipTypeCounts[type] ?? 0}</span>
                </button>
              );
            })}
          </div>

          {/* Connected only */}
          <label
            className="flex items-center gap-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] cursor-pointer select-none"
            title="Show only items that have at least one currently visible relationship."
          >
            <input
              type="checkbox"
              checked={connectedOnly}
              onChange={e => setConnectedOnly(e.target.checked)}
              className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500"
            />
            Connected only
          </label>

          {/* Path analysis toggle (GRAPH_PATH_ANALYSIS) */}
          <button
            onClick={() => {
              if (!pathAnalysisEnabled) return;
              setPathAnalysisMode((v) => !v);
              setPathFocusedNodeId(null);
            }}
            disabled={!pathAnalysisEnabled}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium  border transition-colors ${
              !pathAnalysisEnabled
                ? 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-ter)] cursor-not-allowed opacity-60 dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-dim)]'
                : pathAnalysisMode
                  ? 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40 dark:border-[var(--slate)]/40'
                  : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
            }`}
            title={
              pathAnalysisEnabled
                ? 'Toggle path & impact analysis mode'
                : 'Enable “Graph: Path & Impact Analysis” in Settings → Feature Flags'
            }
          >
            <Zap className="h-3.5 w-3.5" />
            Impact
          </button>

          {/* Nexus proposal count badge (GRAPH_NEXUS_OVERLAY) */}
          {nexusOverlayEnabled && visibleNexusCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border bg-cyan-50 border-cyan-200 text-cyan-700 dark:bg-cyan-950/30 dark:border-cyan-700/40 dark:text-cyan-300">
              <Sparkles className="h-3.5 w-3.5" />
              {visibleNexusCount} Nexus
            </span>
          )}

          {/* Layout */}
          <div className="flex items-center gap-0.5 bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] p-0.5 border border-transparent dark:border-[var(--rule)]">
            {(['hierarchy', 'grid'] as LayoutMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setLayoutMode(mode)}
                className={`px-2.5 py-1 text-xs font-medium  transition-colors capitalize ${
                  layoutMode === mode
                    ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm dark:bg-[var(--surface)] dark:text-[var(--text-pri)] dark:shadow-none'
                    : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>

          {/* Card config toggle */}
          <button
            onClick={() => setShowCardConfig(v => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium  border transition-colors ${
              showCardConfig
                ? 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40 dark:border-[var(--slate)]/40'
                : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
            }`}
            title="Configure card fields"
          >
            <Settings2 className="h-3.5 w-3.5" />
            Card fields
          </button>

          {/* Clear filters */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
              title="Clear filters"
            >
              <X className="h-4 w-4" />
            </button>
          )}
            </>
          )}
        </div>

        {/* Card config panel — inline below toolbar */}
        {showCardConfig && (
          <div className="w-full">
            <CardConfigPanel config={cardConfig} onChange={setCardConfig} />
          </div>
        )}

        {/* Path analysis hint */}
        {pathAnalysisEnabled && pathAnalysisMode && (
          <div className="w-full flex items-center gap-2 text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)]/30 px-3 py-2">
            <Info className="h-3.5 w-3.5 flex-shrink-0" />
            Click any node to trace its full upstream and downstream reach across all relationship types.
            <span className="ml-auto flex items-center gap-1 text-[10px]">
              <span className="inline-block w-3 h-0.5 bg-[var(--slate)] rounded" /> source
              <span className="ml-2 inline-block w-3 h-0.5 bg-[var(--slate)] rounded" /> hop 1
              <span className="ml-2 inline-block w-3 h-0.5 bg-cyan-500 rounded" /> hop 2
            </span>
          </div>
        )}
      </div>

      {/* Graph canvas */}
      <div className="flex-1 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden relative">
        {displayItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <GitBranch className="h-12 w-12 text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-4" />
            <p className="text-[var(--text-ter)] dark:text-[var(--text-sec)] font-medium">No items to display</p>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
              {activeItems.length === 0
                ? 'No active items in this workspace'
                : 'Adjust your filters or add relationships'}
            </p>
          </div>
        ) : (
          <ReactFlowProvider>
            <GraphInner
              filteredItems={displayItems}
              filteredEdges={displayEdges}
              layoutMode={layoutMode}
              totalItemCount={filteredItems.length}
              cardConfig={cardConfig}
              onNewEdge={handleNewEdge}
              onRemoveEdges={handleRemoveEdges}
              onFocusChange={handleNodeFocus}
              nodeExtras={nodeExtras}
              nexusEdges={nexusFlowEdges}
              pathAnalysisMode={pathAnalysisEnabled && pathAnalysisMode}
              pathReachResult={pathReachResult}
            />
          </ReactFlowProvider>
        )}

        {/* Read-only pool diagnostics; session enforcement remains feature-flagged. */}
        <div className="absolute top-3 left-3 z-20">
          <PoolViolationsPanel
            options={poolOptions}
            selectedId={selectedPoolId}
            onSelect={(id) => { setSelectedPoolId(id); setActiveViolationKey(null); setHighlightItemIds(null); }}
            report={violationReport}
            loading={poolLoading}
            activeViolationKey={activeViolationKey}
            onHighlight={(ids, key) => {
              setActiveViolationKey(key);
              setHighlightItemIds(ids ? new Set(ids) : null);
            }}
          />
        </div>

        {/* Item Inspector Panel (GRAPH_INSPECTOR) */}
        {inspectorEnabled && inspectorNodeId && (
          <ItemInspectorPanel
            itemId={inspectorNodeId}
            allItems={activeItems}
            edges={relationshipEdges}
            groups={groups}
            groupMemberships={groupMemberships}
            lineageEvents={lineageEvents}
            onClose={() => setInspectorNodeId(null)}
            presentation={isTablet ? 'sheet' : 'sidebar'}
          />
        )}

        {/* Connection type picker overlay */}
        {pendingConn && (
          <div className="absolute inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.15)' }}>
            <ConnectionTypePicker
              pending={pendingConn}
              onSelect={handleEdgeTypeSelect}
              onCancel={() => setPendingConn(null)}
            />
          </div>
        )}
      </div>

      {/* Cluster Resolution Modal (GRAPH_CLUSTER_RESOLUTION) */}
      {clusterResEnabled && clusterGroupId && clusterGroup && (
        <ClusterResolutionModal
          groupId={clusterGroupId}
          group={clusterGroup}
          memberships={clusterMemberships}
          allItems={activeItems}
          onResolve={handleClusterResolve}
          onClose={() => setClusterGroupId(null)}
        />
      )}
    </div>
  );
}
